import { useMemo } from 'react'
import styled, { css, keyframes } from 'styled-components'

export type PinState = 'idle' | 'error' | 'loading' | 'success'

type PinDotsProps = {
  fillCount: number
  length: number
  state: PinState
}

export const PinDots: React.FC<PinDotsProps> = ({
  length = 4,
  fillCount,
  state,
}) => {
  const items = useMemo(() => Array.from({ length }).fill(0), [length])
  return (
    <Root>
      {items.map((_, idx) => (
        <Dot key={idx} $filled={idx < fillCount} $state={state}></Dot>
      ))}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-gap: 18px;
`

const ShakeFrames = keyframes`
    10%, 90% {
        transform: translate3d(-1px, 0, 0);
    }

    20%, 80% {
      transform: translate3d(2px, 0, 0);
    }

    30%, 50%, 70% {
      transform: translate3d(-4px, 0, 0);
    }

    40%, 60% {
      transform: translate3d(4px, 0, 0);
    }
`

const ScaleFrames = keyframes`
    0%, 100% {
      transform: scale(1);
    }

    50% { 
      transform: scale(1.2);
    }
`

const Dot = styled.div<{ $filled: boolean; $state: PinState }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;

  border: 1.5px solid var(--background-12);
  transition: all 0.15s linear;

  ${({ $filled, $state }) => {
    if (!$filled) {
      return
    }

    if ($state === 'success') {
      return css`
        background: var(--success-8);
        border-color: transparent;
        animation: ${ScaleFrames} 0.5s ease-in-out both;
      `
    }

    if ($state === 'error') {
      return css`
        background: var(--error-8);
        border-color: transparent;
        animation: ${ShakeFrames} 0.6s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
      `
    }

    return css`
      background: var(--text-12);
    `
  }}
`
